import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, siteInfo } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function ArticleSEO({ comic }: { comic: Comic }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${siteInfo.name}`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate || comic.updateDate,
    dateModified: comic.updateDate,
    image: comic.coverImage,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
        { '@type': 'ListItem', position: 2, name: `${comic.categoryName}漫画`, item: `${siteInfo.url}/category/${comic.category}` },
        { '@type': 'ListItem', position: 3, name: comic.title },
      ],
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function Article() {
  const { articleId } = useParams<{ articleId: string }>();
  const comic = comics.find((c) => c.id === articleId);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除。</p>
            <Link
              to="/"
              className="px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <ArticleSEO comic={comic} />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.categoryName}漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="mt-6">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="w-full md:w-[320px] flex-shrink-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden sticky top-24">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-500">
                    <i className="ri-user-line mr-1"></i>
                    {comic.author}
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-folder-line mr-1"></i>
                    {comic.categoryName}
                  </span>
                  <span className="text-sm text-accent-500 font-semibold">
                    <i className="ri-star-fill mr-1"></i>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {Math.floor(comic.views / 10000)}万阅读
                  </span>
                  {comic.isCompleted && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                      已完结
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 bg-background-100/70 rounded-xl p-5">
                  <h2 className="text-base font-semibold text-foreground-900 mb-3">
                    作品简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground-900">
                      章节列表
                      <span className="text-sm font-normal text-foreground-500 ml-2">
                        共 {comic.chapters.length} 话
                      </span>
                    </h2>
                    <span className="text-xs text-foreground-500">
                      更新于 {comic.updateDate}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2 scrollbar-hide">
                    {[...comic.chapters].reverse().map((chapter) => (
                      <div
                        key={chapter.num}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-foreground-900">
                            第 {chapter.num} 话
                          </span>
                          <span className="text-sm text-foreground-600">
                            {chapter.title}
                          </span>
                        </div>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {chapter.updateDate}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-4">
                    <button className="flex-1 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                      开始阅读第1话
                    </button>
                    <button className="px-6 py-3 border border-foreground-300 text-foreground-700 rounded-full text-sm font-medium hover:border-primary-400 hover:text-primary-500 transition-all duration-300 whitespace-nowrap cursor-pointer">
                      收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-14">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}